/**
 * Unified logging service combining application logging and analytics
 */
import config from '../config';

export const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4,
};

class UnifiedLogger {
  constructor() {
    this.level = this.getLogLevel();
    this.context = {};
    this.listeners = [];
    this.sessionId = this.getSessionId();
    this.userId = this.getCurrentUserId();
    this.eventQueue = [];
    this.isOnline = navigator.onLine;

    this.setupNetworkListeners();
    this.setupPeriodicFlush();
    this.setupPageUnloadHandler();
  }

  /**
   * Get current log level based on environment
   */
  getLogLevel() {
    if (config.NODE_ENV === 'production') {
      return LogLevel.ERROR;
    } else if (config.NODE_ENV === 'test') {
      return LogLevel.NONE;
    } else {
      return LogLevel.DEBUG;
    }
  }

  /**
   * Setup network status listeners
   */
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushEventQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  /**
   * Setup periodic queue flushing
   */
  setupPeriodicFlush() {
    setInterval(() => this.flushEventQueue(), 30000);
  }

  /**
   * Setup page unload handler
   */
  setupPageUnloadHandler() {
    window.addEventListener('beforeunload', () => {
      this.flushEventQueue();
    });
  }

  /**
   * Set global context for all log messages
   */
  setContext(context) {
    this.context = { ...this.context, ...context };
  }

  /**
   * Add log listener for external logging services
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove log listener
   */
  removeListener(callback) {
    const index = this.listeners.indexOf(callback);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * Create log entry with metadata
   */
  createLogEntry(level, message, data = {}, error = null) {
    const entry = {
      timestamp: new Date().toISOString(),
      level: Object.keys(LogLevel)[level],
      message,
      data: { ...this.context, ...data },
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      userId: this.userId,
    };

    if (error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    return entry;
  }

  /**
   * Send log to external services
   */
  sendToExternalServices(entry) {
    this.listeners.forEach(callback => {
      try {
        callback(entry);
      } catch (e) {
        // Don't let listener errors break logging
      }
    });

    if (config.NODE_ENV === 'production' && entry.level === 'ERROR') {
      this.sendToErrorTracking(entry);
    }
  }

  /**
   * Send error to tracking service
   */
  sendToErrorTracking(entry) {
    try {
      const errors = JSON.parse(localStorage.getItem('app_logs') || '[]');
      errors.push(entry);

      if (errors.length > 100) {
        errors.splice(0, errors.length - 100);
      }

      localStorage.setItem('app_logs', JSON.stringify(errors));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  /**
   * Log debug message
   */
  debug(message, data = {}) {
    if (this.level <= LogLevel.DEBUG) {
      const entry = this.createLogEntry(LogLevel.DEBUG, message, data);
      if (config.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug(`[DEBUG] ${message}`, entry.data);
      }
      this.sendToExternalServices(entry);
    }
  }

  /**
   * Log info message
   */
  info(message, data = {}) {
    if (this.level <= LogLevel.INFO) {
      const entry = this.createLogEntry(LogLevel.INFO, message, data);
      if (config.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.info(`[INFO] ${message}`, entry.data);
      }
      this.sendToExternalServices(entry);
    }
  }

  /**
   * Log warning message
   */
  warn(message, data = {}) {
    if (this.level <= LogLevel.WARN) {
      const entry = this.createLogEntry(LogLevel.WARN, message, data);
      if (config.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(`[WARN] ${message}`, entry.data);
      }
      this.sendToExternalServices(entry);
    }
  }

  /**
   * Log error message
   */
  error(message, error = null, data = {}) {
    if (this.level <= LogLevel.ERROR) {
      const entry = this.createLogEntry(LogLevel.ERROR, message, data, error);
      if (config.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error(`[ERROR] ${message}`, error || entry.data);
      }
      this.sendToExternalServices(entry);
    }
  }

  // Analytics Methods

  /**
   * Log a general event
   */
  logEvent(eventName, properties = {}, category = 'general') {
    const event = {
      name: eventName,
      category,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        url: window.location.href,
        referrer: document.referrer,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    };

    this.queueEvent(event);
  }

  /**
   * Log page view event
   */
  logPageView(pageName, properties = {}) {
    this.logEvent(
      'page_view',
      {
        page_name: pageName,
        page_url: window.location.href,
        page_title: document.title,
        ...properties,
      },
      'navigation',
    );
  }

  /**
   * Log user interaction event
   */
  logUserInteraction(action, element, properties = {}) {
    this.logEvent(
      'user_interaction',
      {
        action,
        element,
        ...properties,
      },
      'interaction',
    );
  }

  /**
   * Log e-commerce events
   */
  logEcommerceEvent(eventType, eventData) {
    const ecommerceEvents = {
      product_viewed: data => ({
        item_id: data.itemId,
        item_name: data.itemName,
        price: data.price,
        category: data.category,
        view_type: data.viewType || 'list',
      }),
      add_to_cart: data => ({
        item_id: data.itemId,
        item_name: data.itemName,
        price: data.price,
        quantity: data.quantity,
        category: data.category,
        size: data.size,
        color: data.color,
      }),
      remove_from_cart: data => ({
        item_id: data.itemId,
        item_name: data.itemName,
        quantity: data.quantity,
      }),
      begin_checkout: data => ({
        cart_value: data.cartValue,
        item_count: data.itemCount,
        items: data.items,
      }),
      purchase: data => ({
        transaction_id: data.transactionId,
        value: data.value,
        currency: data.currency || 'USD',
        items: data.items,
        shipping: data.shipping,
        tax: data.tax,
      }),
    };

    if (ecommerceEvents[eventType]) {
      const processedData = ecommerceEvents[eventType](eventData);
      this.logEvent(eventType, processedData, 'ecommerce');
    } else {
      this.logEvent(eventType, eventData, 'ecommerce');
    }
  }

  /**
   * Queue event for later sending
   */
  queueEvent(event) {
    this.eventQueue.push(event);

    if (this.isOnline && this.eventQueue.length >= 10) {
      this.flushEventQueue();
    }
  }

  /**
   * Flush event queue to server
   */
  async flushEventQueue() {
    if (this.eventQueue.length === 0 || !this.isOnline) {
      return;
    }

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    try {
      if (config.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.group('📊 Analytics Events');
        eventsToSend.forEach(event => {
          // eslint-disable-next-line no-console
          console.log(`${event.category}:${event.name}`, event.properties);
        });
        // eslint-disable-next-line no-console
        console.groupEnd();
      }

      await this.sendToAnalyticsService(eventsToSend);
    } catch (error) {
      this.eventQueue.unshift(...eventsToSend);
      // eslint-disable-next-line no-console
      console.warn('Failed to send analytics events:', error);
    }
  }

  /**
   * Send events to analytics service
   */
  async sendToAnalyticsService(events) {
    try {
      const storedEvents = JSON.parse(localStorage.getItem('analyticsEvents') || '[]');
      storedEvents.push(...events);

      if (storedEvents.length > 1000) {
        storedEvents.splice(0, storedEvents.length - 1000);
      }

      localStorage.setItem('analyticsEvents', JSON.stringify(storedEvents));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to store analytics events:', error);
    }
  }

  /**
   * Get current user ID
   */
  getCurrentUserId() {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.user_id || payload.sub;
      }
    } catch (error) {
      // Ignore token parsing errors
    }
    return null;
  }

  /**
   * Get or create session ID
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Update user ID when user logs in/out
   */
  setUserId(userId) {
    this.userId = userId;
  }

  /**
   * Get stored logs
   */
  getLogs() {
    try {
      return JSON.parse(localStorage.getItem('app_logs') || '[]');
    } catch (e) {
      return [];
    }
  }

  /**
   * Clear stored logs
   */
  clearLogs() {
    try {
      localStorage.removeItem('app_logs');
      localStorage.removeItem('analyticsEvents');
    } catch (e) {
      // Ignore localStorage errors
    }
  }
}

// Create singleton instance
const logger = new UnifiedLogger();

// Development utilities
if (config.NODE_ENV === 'development') {
  window.logger = logger;

  window.debugUtils = {
    showLogs: () => {
      const logs = logger.getLogs();
      // eslint-disable-next-line no-console
      console.table(logs);
      return logs;
    },
    clearLogs: () => {
      logger.clearLogs();
      // eslint-disable-next-line no-console
      console.log('All logs cleared');
    },
    testLogging: () => {
      logger.debug('Debug message', { test: true });
      logger.info('Info message', { test: true });
      logger.warn('Warning message', { test: true });
      logger.error('Error message', new Error('Test error'), { test: true });
      logger.logEvent('test_event', { test: true });
      // eslint-disable-next-line no-console
      console.log('All logging tested');
    },
  };

  logger.info('Application started', {
    environment: config.NODE_ENV,
    timestamp: new Date().toISOString(),
  });
}

export default logger;
